import { html, TemplateResult } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_MANUAL_ADD_EXCEPTION_CONTAINER } from '../../../../../../../../webcomponents.constants';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import {
  getDeviceControlExceptionTypes,
  getDeviceControlPermissions,
} from '../../../../../utils/uiSettings/deviceControl/device-control.values';
import { InputWidthValues } from '../../../../../../../../ui/gzInput';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../ui/gzChipsGeneratorInput';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import {
  DeviceControlExclusionRuleForm,
  DeviceControlExclusionRuleItem,
} from '@nirvana/shared/dtos/types/policies/common';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import _ from 'lodash';
import { styles } from '../view/styles';

@customElement(WEB_COMPONENT_MANUAL_ADD_EXCEPTION_CONTAINER)
export class ManualAddExceptionContainerWebComponent extends GzPolicyFullPageWindowFormWebComponent<DeviceControlExclusionRuleForm> {
  private allItems: DeviceControlExclusionRuleItem[];
  private exclusionRuleForm: DeviceControlExclusionRuleForm;
  constructor(allItems: DeviceControlExclusionRuleItem[], exclusionRuleForm: DeviceControlExclusionRuleForm) {
    super();
    this.allItems = allItems;
    this.exclusionRuleForm = exclusionRuleForm;
  }

  @query('#type-form-field')
  type: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#exclusions-form-field')
  exclusions: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#chips-generator-input')
  chipsGeneratorInput: GzChipsGeneratorInputWebComponent;
  @query('#description-form-field')
  description: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#permission-form-field')
  permission: GzFormFieldWebComponent<GzDropdownWebComponent>;

  static get styles() {
    return styles;
  }

  protected createFormGroup(): void {
    this.type.Validators = [RequiredValidator(translateFn('Required field'))];

    this.exclusions.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator(this.validateDuplicates.bind(this)),
    ];
    this.description.Validators = [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(
        80,
        translateFn('The description must have a maximum of {maxLength} characters.', {
          maxLength: 80,
        })
      ),
    ];

    this.permission.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = new FormGroup({
      type: this.type,
      exclusions: this.exclusions,
      description: this.description,
      permission: this.permission,
    });
  }

  setFormGroupValue(): void {
    this.formGroup.value = {
      type: this.exclusionRuleForm.type,
      exclusions: this.exclusionRuleForm.exclusions,
      description: this.exclusionRuleForm.description,
      permission: this.exclusionRuleForm.permission,
    };
  }

  protected renderContent(): TemplateResult {
    return html`<div style="padding-top: calc(var(--spacer) * 2)">
      <gz-form-field id="type-form-field" class="label-mw-250 mt-2" label="${translateFn('Exclusion type')}" required>
        <gz-dropdown
          id="permission-dropdown"
          gz-form-input
          labelWidth="${InputWidthValues.LARGE}"
          .options="${getDeviceControlExceptionTypes()}"
        >
        </gz-dropdown>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id="exclusions-form-field" required>
        <gz-chips-generator-input
          id="chips-generator-input"
          validationRegex="^[^\\s]*$"
          hasFloatingLabel
          .strings="${{
            placeholder: translateFn('Add one ID at a time.'),
            invalidItemErrorMessage: translateFn('Invalid entry'),
            label: translateFn('Exclusions') + ':*',
            clearListButtonText: translateFn('Clear list'),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Delete'),
            deleteWindowTitle: translateFn('Delete IDs?'),
            deleteWindowContentText: translateFn(
              'You are about to delete the IDs displayed in the list. This action cannot be undone.'
            ),
            notUniqueErrorMessage: translateFn('An exclusion rule already exists for this item'),
          }}"
          style="--chip-list-width: 490px;
          --form-field-with-floating-max-width: 490px;"
          gz-form-input
        >
        </gz-chips-generator-input>
        <gz-input-error style="padding-top:10px"></gz-input-error>
      </gz-form-field>
      <gz-form-field class="label-mw-250" id="description-form-field" label="${translateFn('Description')}" required>
        <gz-input
          id="description-input"
          inputWidth="${InputWidthValues.LARGE}"
          placeholder="${translateFn('Add description.')}"
          gz-form-input
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id="permission-form-field" class="label-mw-250 mt-2" label="${translateFn('Permission')}" required>
        <gz-dropdown
          id="permission-dropdown"
          gz-form-input
          labelWidth="${InputWidthValues.LARGE}"
          .options="${getDeviceControlPermissions()}"
        >
          <gz-input-error></gz-input-error>
        </gz-dropdown>
      </gz-form-field>
    </div>`;
  }

  public reset() {}

  private async validateDuplicates(addedExclusions: string[]) {
    const type = this.formGroup.value.type;
    const exceptions = this.allItems.filter(item => item.ruleType === type).map(item => item.exception);
    for (const exclusion of addedExclusions) {
      if (exceptions.includes(exclusion)) {
        return {
          valid: false,
          error: translateFn('An exclusion rule already exists for this item: {exclusionItem}', {
            exclusionItem: exclusion,
          }),
        };
      }
    }
    return {
      valid: true,
    };
  }

  protected getConvertedFormValue(): any {
    const formValue = this.formGroup.value;
    const exclusions: string[] = _.get(formValue, 'exclusions', []) as string[];
    return exclusions.map(item => {
      return {
        ruleType: formValue.type,
        exception: item,
        ruleName: formValue.description,
        permission: formValue.permission,
        userSID: '',
      };
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_MANUAL_ADD_EXCEPTION_CONTAINER: ManualAddExceptionContainerWebComponent;
  }
}
